import {fromSeconds} from 'common/date';
import {DateTime} from 'luxon';
import {
  findDisplayPeriod,
  isCurrentPeriod,
} from 'src/CardBenefitsView/benefitUtils';
import {cardProductLabel} from 'src/cardProductOptions';
import type {FullFeePeriodFragment} from '@transactions/graphql';

export type CardGroup = {
  accountId: string;
  /** Card product name from the display fee period (its product's label,
   * else the free-text payee), or the account name */
  name: string;
  /** The fee period to feature: current cycle, else upcoming, else latest */
  displayPeriod: FullFeePeriodFragment;
  isCurrent: boolean;
  isUpcoming: boolean;
  /** Fee periods starting after the display period (e.g. a prefilled
   * renewal), soonest first */
  upcomingPeriods: FullFeePeriodFragment[];
  /** Fee periods before the display period, newest first */
  pastPeriods: FullFeePeriodFragment[];
};

// Current cards first, then upcoming, then lapsed; alphabetical within each
const groupSortKey = (group: CardGroup): number =>
  group.isCurrent ? 0 : group.isUpcoming ? 1 : 2;

/**
 * Groups fee periods by card (account), featuring each card's current fee
 * period. Cards renew on their own anniversary dates, so this replaces
 * calendar-year grouping: each card shows exactly one featured cycle.
 */
export const groupFeePeriodsByCard = (
  allFeePeriods: FullFeePeriodFragment[],
): CardGroup[] => {
  const byAccount = new Map<string, FullFeePeriodFragment[]>();

  for (const fp of allFeePeriods) {
    const existing = byAccount.get(fp.accountId);
    if (existing) {
      existing.push(fp);
    } else {
      byAccount.set(fp.accountId, [fp]);
    }
  }

  const now = DateTime.now();
  const groups: CardGroup[] = [];

  for (const [accountId, feePeriods] of byAccount) {
    feePeriods.sort((a, b) => b.startDate - a.startDate);
    const display = findDisplayPeriod(feePeriods);
    if (!display) {
      continue;
    }

    const others = feePeriods.filter((fp) => fp !== display);
    groups.push({
      accountId,
      name:
        cardProductLabel(display.cardProduct) ??
        display.payee ??
        display.account.name,
      displayPeriod: display,
      isCurrent: isCurrentPeriod(display.startDate, display.endDate),
      isUpcoming: fromSeconds(display.startDate) > now,
      upcomingPeriods: others
        .filter((fp) => fp.startDate > display.startDate)
        .reverse(),
      pastPeriods: others.filter((fp) => fp.startDate <= display.startDate),
    });
  }

  return groups.sort(
    (a, b) =>
      groupSortKey(a) - groupSortKey(b) || a.name.localeCompare(b.name),
  );
};
